import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';

interface Section {
  n: string;
  title: string;
  paras?: string[];
  bullets?: string[];
}

const LAST_UPDATED = '2026-06-24';

const SECTIONS: Section[] = [
  {
    n: '1',
    title: 'Acceptance of These Terms',
    paras: [
      'These Terms of Service ("Terms") govern your use of the GRID Support portal (the "Service"), operated by GRID LLC, a Wyoming, USA limited liability company ("GRID", "we", "us"). By accessing or using the Service, you agree to these Terms. If you do not agree, do not use the Service.',
    ],
  },
  {
    n: '2',
    title: 'The Service',
    paras: [
      'The Service lets you request services, submit and track support tickets, and — if you choose — participate in the GRID affiliate referral program. We may add, change, or remove features at any time.',
    ],
  },
  {
    n: '3',
    title: 'Accounts and Sign-In',
    paras: [
      'You sign in using Google authentication. You are responsible for maintaining the security of your account and for all activity under it, and you agree to provide accurate information. You must be at least 18 years old to use the Service.',
    ],
  },
  {
    n: '4',
    title: 'Acceptable Use',
    paras: ['You agree not to:'],
    bullets: [
      'Use the Service for any unlawful, fraudulent, or harmful purpose.',
      'Upload malware or content you do not have the right to share.',
      'Attempt to gain unauthorized access to the Service, other accounts, or our systems.',
      'Interfere with, disrupt, scrape, or overload the Service.',
      'Misrepresent your identity or your affiliation with any person or entity.',
    ],
  },
  {
    n: '5',
    title: 'Your Content',
    paras: [
      'You retain ownership of the tickets, messages, and attachments you submit ("Your Content"). You grant GRID a license to host, process, and use Your Content solely to operate and provide the Service. You are responsible for Your Content and confirm you have the rights to share it, including any third-party or confidential information it contains.',
    ],
  },
  {
    n: '6',
    title: 'Affiliate Program',
    paras: [
      'If you participate in the affiliate program, your participation is governed by the Affiliate Program Agreement, which is incorporated into these Terms by reference. Where the two conflict on affiliate matters, the Affiliate Program Agreement controls.',
    ],
  },
  {
    n: '7',
    title: 'Cryptocurrency (RLUSD) Payments Disclaimer',
    paras: [
      'Affiliate commissions are calculated in USD and paid in RLUSD, a US-dollar-pegged stablecoin issued on the XRP Ledger, at the time GRID processes the payout. RLUSD is issued by a third party (Ripple) and is intended to hold a 1:1 value with the US dollar; GRID does not control or guarantee its value, peg, or availability. You are solely responsible for providing a correct XRPL wallet address configured with an RLUSD trustline — payments sent to an address you provide are considered made. You are responsible for your own taxes on any amounts received. Nothing in the Service is financial, investment, legal, or tax advice, and nothing here is an offer of a security or an investment.',
    ],
  },
  {
    n: '8',
    title: 'Third-Party Services',
    paras: [
      'The Service relies on third parties including Google/Firebase, and may use ClickUp and Discord for internal operations. Your use of features that depend on those services may also be subject to their terms. We are not responsible for third-party services.',
    ],
  },
  {
    n: '9',
    title: 'Intellectual Property',
    paras: [
      'The Service, including its software, design, text, and the GRID name and logo, is owned by GRID or its licensors and is protected by law. These Terms do not grant you any right to use GRID branding without our prior written permission.',
    ],
  },
  {
    n: '10',
    title: 'Disclaimers',
    paras: [
      'The Service is provided "as is" and "as available," without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, secure, or error-free.',
    ],
  },
  {
    n: '11',
    title: 'Limitation of Liability',
    paras: [
      'To the maximum extent permitted by law, GRID will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or data, arising from or related to your use of the Service. GRID’s total liability for any claim relating to the Service will not exceed the greater of the amounts GRID paid to you in the 12 months before the claim or USD $100.',
    ],
  },
  {
    n: '12',
    title: 'Indemnification',
    paras: [
      'You will indemnify and hold harmless GRID and its owners, officers, and contractors from claims, losses, and expenses (including reasonable legal fees) arising from your use of the Service, Your Content, or your breach of these Terms or any law or third-party right.',
    ],
  },
  {
    n: '13',
    title: 'Suspension and Termination',
    paras: [
      'We may suspend or terminate your access to the Service at any time, with or without notice, including for violation of these Terms. You may stop using the Service at any time. Sections intended to survive termination (including intellectual property, disclaimers, limitation of liability, indemnification, and governing law) survive.',
    ],
  },
  {
    n: '14',
    title: 'Governing Law and Disputes',
    paras: [
      'These Terms are governed by the laws of the State of Wyoming, USA, without regard to conflict-of-laws rules. Any dispute that cannot be resolved informally will be settled by binding arbitration administered by the American Arbitration Association under its Commercial Arbitration Rules, seated in Wyoming and conducted in English. Disputes will be resolved only on an individual basis, and you and GRID waive any right to participate in a class or representative action. Either party may bring an individual claim in small-claims court or seek injunctive relief to protect intellectual property or confidential information.',
    ],
  },
  {
    n: '15',
    title: 'Changes to These Terms',
    paras: [
      'We may update these Terms from time to time. Material changes will be posted here with an updated date. Continued use of the Service after a change means you accept the updated Terms.',
    ],
  },
];

export function TermsOfServicePage() {
  return (
    <Layout>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <Link to="/" style={{ color: 'var(--text-3)', textDecoration: 'none', fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          ← Back Home
        </Link>

        <div className="terminal-card" style={{ marginTop: '16px' }}>
          <div className="card-header">
            <span className="card-title">Terms of Service</span>
          </div>
          <div className="card-body">
            <p className="prose prose--sm prose--muted">Last updated {LAST_UPDATED}</p>
            <div className="terminal-alert" style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-gold)', color: 'var(--text-2)', fontSize: '11px' }}>
              Template document — have it reviewed by a qualified attorney before relying on it.
            </div>

            {SECTIONS.map((s) => (
              <div key={s.n} style={{ marginTop: '6px' }}>
                <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>
                  {s.n}. {s.title}
                </div>
                {s.paras?.map((p, i) => (
                  <p key={i} className="prose prose--sm prose--muted">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="rules-list" style={{ marginTop: '4px' }}>
                    {s.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            <p className="prose prose--sm" style={{ marginTop: '12px', color: 'var(--text)' }}>
              <strong>Contact:</strong> admin@gridservices.xyz
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
